import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

interface SidebarContextValue {
  close: () => void
  companySlug: string | null
  isOpen: boolean
  open: () => void
  registerReload: (reload: () => void) => () => void
  registerSidebar: (companySlug: string) => void
  reloadItems: () => void
  toggle: () => void
  unregisterSidebar: () => void
}

const SidebarContext = createContext<SidebarContextValue | null>(null)

SidebarContext.displayName = 'SidebarContext'

const noop = (): void => undefined

interface SidebarContextProviderProps {
  children: React.ReactNode
}

export const SidebarContextProvider: React.FC<SidebarContextProviderProps> = ({ children }) => {
  const [companySlug, setCompanySlug] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const reloadRef = useRef<() => void>(noop)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((current) => !current), [])

  const registerReload = useCallback((reload: () => void) => {
    reloadRef.current = reload
    return () => {
      if (reloadRef.current === reload) reloadRef.current = noop
    }
  }, [])

  const reloadItems = useCallback(() => {
    reloadRef.current()
  }, [])

  const registerSidebar = useCallback((slug: string) => {
    setCompanySlug((current) => (current === slug ? current : slug))
  }, [])

  const unregisterSidebar = useCallback(() => {
    setCompanySlug(null)
    setIsOpen(false)
  }, [])

  const value = useMemo(
    () => ({
      close,
      companySlug,
      isOpen,
      open,
      registerReload,
      registerSidebar,
      reloadItems,
      toggle,
      unregisterSidebar,
    }),
    [close, companySlug, isOpen, open, registerReload, registerSidebar, reloadItems, toggle, unregisterSidebar]
  )

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

export const useSidebarContext = (): SidebarContextValue => {
  const value = useContext(SidebarContext)
  if (!value) throw new Error('Sidebar context must be initialized before using it')
  return value
}

export const useRegisterSidebar = (companySlug: string): void => {
  const { registerSidebar, unregisterSidebar } = useSidebarContext()

  useEffect(() => {
    registerSidebar(companySlug)
    return () => unregisterSidebar()
  }, [companySlug, registerSidebar, unregisterSidebar])
}

export const useRegisterSidebarReload = (reload: () => void): void => {
  const { registerReload } = useSidebarContext()

  useEffect(() => registerReload(reload), [registerReload, reload])
}
